import { call, all, put, takeLatest, select, delay } from 'redux-saga/effects';

import history from '~/services/history';
import notification from '~/services/notification';
import orderSplitService from './order-split-service';
import domainService from '~/services/modules/domain/domain-service';

import { ISA_ERROR } from '~/constants';
import { sort } from '../order/order-utils';
import { formatMessage } from '~/i18n/translate';
import * as orderSplitActions from './order-split-actions';
import {
  GET_ALL_REQUEST,
  GET_BY_ID_REQUEST,
  SAVE_REQUEST,
  EDIT_ITEM,
  DELETE_ITEM,
  GET_PRODUCTS_REQUEST
} from './order-split-constants';
import { ConfigurationInterface } from '@e3labs/isa-data-dictionary';
import { AppStateType } from '../root-reducer';
import axios from 'axios';

const INITIAL_VALUES = {
  originSubsidiaryExternalCode: '',
  sectionExternalCodes: [],
  products: [],
  finalSubsidiaryExternalCode: ''
};

let cancel: any;
const CancelToken = axios.CancelToken;

export function* getAllOrderSplit() {
  try {
    yield put(orderSplitActions.setLoadingData(true));

    const { data } = yield call(orderSplitService.getAllOrderSplit);

    if (data) {
      yield put(orderSplitActions.getAllSuccess([...data]));
    }
    yield put(orderSplitActions.setLoadingData(false));
  } catch (error) {
    yield put(orderSplitActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* getById({ id }: any) {
  try {
    yield put(orderSplitActions.setLoadingData(true));
    const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);
    const user = yield select((state: AppStateType) => state.auth.user);

    const [{ data: sections }, { data: products }, { data: subsidiaries }] = yield all([
      call(domainService.getSections),
      call(orderSplitService.postProductsMinimized, {
        skip: 0,
        limit: 20,
        searchEngine: configuration.searchEngine
      }),
      call(domainService.getSubsidiaries, { userSubsidiaryFilter: user?.userSubsidiaries ?? [] })
    ]);

    let selects = {
      subsidiary: sort(subsidiaries.map(({ name, externalCode }) => ({ externalCode, name }))),
      section: sort(sections) ?? [],
      products: sort(products) ?? []
    };

    if (!id) {
      yield put(
        orderSplitActions.getByIdSuccess({
          data: INITIAL_VALUES,
          selects
        })
      );
    } else {
      const { data } = yield call(orderSplitService.getOrderSplit, id);

      const { data: productsInOrderSplit } = yield call(orderSplitService.postProductsMinimized, {
        skip: 0,
        limit: data?.productExternalCodes?.length,
        searchEngine: configuration.searchEngine,
        productExternalCodes: data?.productExternalCodes?.join(',') ?? ''
      });

      const parsedData = {
        ...data,
        originSubsidiaryExternalCode: subsidiaries.find(
          subsidiary => subsidiary.externalCode === data.originSubsidiaryExternalCode
        ),
        finalSubsidiaryExternalCode: subsidiaries.find(
          subsidiary => subsidiary.externalCode === data.finalSubsidiaryExternalCode
        ),
        sectionExternalCodes: data.sectionExternalCodes.map(sectionExternalCode => {
          const section = sections.find(section => section.externalCode === sectionExternalCode);
          return section;
        }),
        products: data?.productExternalCodes
          ?.map(productExternalCode =>
            productsInOrderSplit?.find(product => product?.externalCode === productExternalCode)
          )
          ?.filter(Boolean)
      };

      if (data) {
        yield put(
          orderSplitActions.getByIdSuccess({
            data: parsedData,
            selects
          })
        );
      }
    }
    yield put(orderSplitActions.setLoadingData(false));
  } catch (error) {
    yield put(orderSplitActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* save(data: any) {
  try {
    const { id, payload } = data;
    yield put(orderSplitActions.setLoadingSend(true));

    const parsedPayload = {
      originSubsidiaryExternalCode: payload?.originSubsidiaryExternalCode?.externalCode,
      brandExternalCodes: payload?.brandExternalCodes?.map(b => b.externalCode) ?? [],
      categoryExternalCodes: payload?.categoryExternalCodes?.map(c => c.externalCode) ?? [],
      departmentExternalCodes: payload?.departmentExternalCodes?.map(d => d.externalCode) ?? [],
      providerExternalCodes: payload?.providerExternalCodes?.map(p => p.externalCode) ?? [],
      sectionExternalCodes: payload?.sectionExternalCodes?.map(s => s.externalCode) ?? [],
      finalSubsidiaryExternalCode: payload?.finalSubsidiaryExternalCode?.externalCode,
      productExternalCodes: payload?.products?.map(p => p.externalCode) ?? []
    };

    if (id) {
      yield call(orderSplitService.editOrderSplit, { _id: id, ...parsedPayload });
    } else {
      yield call(orderSplitService.createOrderSplit, parsedPayload);
    }

    yield call(history.push, `/register/order-split/`, {
      date: new Date().getTime()
    });

    yield put(orderSplitActions.setLoadingSend(false));
    yield call(
      notification.success,
      id ? formatMessage('orderSplitFormUpdatedSuccess') : formatMessage('orderSplitFormCreatedSuccess')
    );
  } catch (error) {
    yield put(orderSplitActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* deleteItem({ id }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('commonLabelExcludeItem')
    );
    if (isConfirmed) {
      yield put(orderSplitActions.setLoadingSend(true));
      yield call(orderSplitService.deleteOrderSplit, id);
      yield put(orderSplitActions.setLoadingSend(false));

      yield put(orderSplitActions.getAllRequest());
      yield call(notification.success, formatMessage('orderSplitFormExcludeSuccess'));
    }
  } catch (error) {
    yield put(orderSplitActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* editItem({ id }: any) {
  yield call(history.push, `/register/order-split/edit/${id}`);
}

export function* getProducts({ queryString, skip }: any) {
  yield delay(200);

  if (cancel !== undefined) cancel();

  const cancelToken = new CancelToken(function executor(c) {
    cancel = c;
  });

  const { selects } = yield select((state: AppStateType) => state.orderSplit.form);
  const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);

  try {
    yield put(orderSplitActions.setLoadingSelects(true));

    const params = {
      skip: skip === 0 ? 0 : skip,
      limit: 25,
      queryString,
      searchEngine: configuration.searchEngine
    };
    const { data: products } = yield call(
      orderSplitService.postProductsMinimized,
      {
        ...params
      },
      {
        cancelToken
      }
    );

    const existingProductIds = selects?.products?.map(product => product?.externalCode);
    const filteredProducts = products?.filter(product => !existingProductIds?.includes(product?.externalCode));
    const parsedProducts =
      [...selects?.products, ...filteredProducts]?.sort((a, b) => a?.name.localeCompare(b?.name)) ?? [];

    yield put(orderSplitActions.getProductsSuccess(parsedProducts));
  } catch (error) {
    yield put({ type: ISA_ERROR, error });
  } finally {
    yield put(orderSplitActions.setLoadingSelects(false));
  }
}

export default all([
  takeLatest(GET_ALL_REQUEST, getAllOrderSplit),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(SAVE_REQUEST, save),
  takeLatest(DELETE_ITEM, deleteItem),
  takeLatest(EDIT_ITEM, editItem),
  takeLatest(GET_PRODUCTS_REQUEST, getProducts)
]);
